import { useMemo } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Notice } from '@verlo/shared/components/ui/notice';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';

/**
 * A meal's groups (meals plan, 30 Sep 2026): a kids meal, a lunch combo, a set menu.
 *
 * It looks like the modifier editor beside it, and the one difference is the whole point:
 * an option here is **another menu item**, picked from the menu, not a word somebody types.
 * That is what lets the bill show one line while the kitchen gets the parts — each routed
 * by its own section, so the juice reaches the bar — and what lets cost, allergens, PLU and
 * stock still see through to the things actually made.
 *
 * The price on an option is what the **meal** costs more, not what the dish sells for on its
 * own: a £6.95 kids meal whose milkshake is +£1.00 is £7.95, whatever a milkshake is.
 *
 * Shape: [{id, label, rule: 'one'|'any', min?, max?, options: [{menu_item_id, name,
 * category, price_delta, default?}]}].
 */

const uid = () => Math.random().toString(36).slice(2, 10);

const MealGroupsEditor = ({ value = [], onChange, menuItems = [], selfId = null, basePrice = 0 }) => {
  const groups = value;

  // A meal cannot be made of itself, and a meal of meals is a hall of mirrors the kitchen
  // would have to unwind — components are plain dishes.
  const pickable = useMemo(
    () => menuItems
      .filter((m) => m.id !== selfId && !(m.meal_groups || []).length)
      .sort((a, b) => (a.category || '').localeCompare(b.category || '') || a.name.localeCompare(b.name)),
    [menuItems, selfId],
  );
  const byId = useMemo(() => Object.fromEntries(pickable.map((m) => [m.id, m])), [pickable]);

  const set = (i, patch) => onChange(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const setOpt = (i, k, patch) => set(i, { options: groups[i].options.map((o, m) => (m === k ? { ...o, ...patch } : o)) });
  const addGroup = (rule) => onChange([...groups, {
    id: `mg-${uid()}`, label: rule === 'one' ? 'Choice' : 'Make it a meal', rule, options: [],
  }]);
  const addOpt = (i, menuItemId) => {
    const row = byId[menuItemId];
    if (!row || groups[i].options.some((o) => o.menu_item_id === menuItemId)) return;
    set(i, {
      options: [...groups[i].options, {
        menu_item_id: row.id, name: row.name, category: row.category || '', price_delta: 0,
        default: groups[i].rule === 'one' && groups[i].options.length === 0,
      }],
    });
  };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= groups.length) return;
    const next = [...groups];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  // The cheapest way the meal can be sold, and the dearest — the two numbers somebody
  // setting a combo price actually wants, and neither is the sum of the à la carte prices.
  const [low, high] = useMemo(() => {
    let lo = Number(basePrice) || 0;
    let hi = lo;
    for (const g of groups) {
      const deltas = (g.options || []).map((o) => Number(o.price_delta) || 0);
      if (!deltas.length) continue;
      if ((g.rule || 'one') === 'one') {
        lo += Math.min(...deltas);
        hi += Math.max(...deltas);
      } else {
        const up = deltas.filter((d) => d > 0);
        hi += g.max != null ? up.sort((a, b) => b - a).slice(0, Number(g.max)).reduce((s, d) => s + d, 0)
          : up.reduce((s, d) => s + d, 0);
        lo += (g.min != null && Number(g.min) > 0)
          ? deltas.slice().sort((a, b) => a - b).slice(0, Number(g.min)).reduce((s, d) => s + d, 0) : 0;
      }
    }
    return [lo, hi];
  }, [groups, basePrice]);

  const money = (v) => `£${(Number(v) || 0).toFixed(2)}`;

  return (
    <div className="space-y-3" data-testid="meal-groups">
      {groups.length === 0 && (
        <p className="text-[13px] text-ink-faint">
          Not a meal. Add a group to make this item one: a kids meal, a lunch combo, a set
          menu. Each choice is another item on the menu, so the kitchen still gets the parts.
        </p>
      )}

      {groups.map((g, i) => {
        const rule = g.rule || 'one';
        const defaults = (g.options || []).filter((o) => o.default).length;
        return (
          <div key={g.id} className="rounded-lg border border-hairline bg-surface-2 p-3" data-testid={`meal-group-${i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Input value={g.label} onChange={(e) => set(i, { label: e.target.value })} className="h-9 w-44 bg-white font-semibold" placeholder="Group name" data-testid={`meal-group-${i}-label`} />
              <Segmented value={rule} onChange={(v) => set(i, { rule: v })} options={[{ value: 'one', label: 'Choose one' }, { value: 'any', label: 'Optional' }]} />
              {rule === 'any' && (
                <div className="flex items-center gap-1.5 text-[12.5px] text-ink-soft">
                  <span>at least</span>
                  <Input type="number" min="0" inputMode="numeric" value={g.min ?? ''} onChange={(e) => set(i, { min: e.target.value === '' ? null : Number(e.target.value) })} className="h-9 w-16 bg-white font-mono tabular-nums" placeholder="0" aria-label="At least" />
                  <span>at most</span>
                  <Input type="number" min="1" inputMode="numeric" value={g.max ?? ''} onChange={(e) => set(i, { max: e.target.value === '' ? null : Number(e.target.value) })} className="h-9 w-16 bg-white font-mono tabular-nums" placeholder="any" aria-label="At most" />
                </div>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(i, -1)} aria-label="Move up"><ChevronUp size={14} /></Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(i, 1)} aria-label="Move down"><ChevronDown size={14} /></Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-ink-faint hover:text-signal-stop" onClick={() => onChange(groups.filter((_, j) => j !== i))} aria-label="Remove group"><X size={14} /></Button>
              </div>
            </div>

            <div className="mt-2 grid gap-1.5">
              {g.options.length > 0 && (
                <div className="grid grid-cols-[1fr_120px_96px_60px_28px] gap-2 px-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-faint">
                  <span>Dish</span><span>Section</span><span>Costs more</span><span>{rule === 'one' ? 'Default' : ''}</span><span />
                </div>
              )}
              {g.options.map((o, k) => (
                <div key={o.menu_item_id} className="grid grid-cols-[1fr_120px_96px_60px_28px] items-center gap-2" data-testid={`meal-group-${i}-option-${k}`}>
                  <span className="truncate text-[13.5px] font-medium text-ink">{byId[o.menu_item_id]?.name || o.name}</span>
                  <span className="truncate text-[12.5px] text-ink-faint">{byId[o.menu_item_id]?.category || o.category || '—'}</span>
                  <Input type="number" step="0.01" inputMode="decimal" value={o.price_delta ?? 0} onChange={(e) => setOpt(i, k, { price_delta: e.target.value })} className="h-9 bg-white font-mono tabular-nums" placeholder="0.00" title={`The meal becomes ${money((Number(basePrice) || 0) + (Number(o.price_delta) || 0))} with this chosen`} />
                  {rule === 'one'
                    ? <Switch checked={!!o.default} onCheckedChange={(v) => set(i, { options: g.options.map((x, m) => ({ ...x, default: m === k ? v : false })) })} aria-label="Default" />
                    : <span />}
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-ink-faint hover:text-signal-stop" onClick={() => set(i, { options: g.options.filter((_, m) => m !== k) })} aria-label="Remove choice"><X size={14} /></Button>
                </div>
              ))}

              {/* A dish that has itself been delisted still reads by the name the meal was
                  sold under, so nobody is left guessing what a blank row used to be. */}
              {g.options.some((o) => !byId[o.menu_item_id]) && (
                <p className="px-1 text-[12px] text-signal-warn">
                  A choice here is no longer on the menu. Remove it, or put the dish back.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Select value="" onValueChange={(id) => addOpt(i, id)}>
                  <SelectTrigger className="h-9 w-64 bg-white text-[12.5px]" data-testid={`meal-group-${i}-add`}>
                    <SelectValue placeholder="Add a dish from the menu…" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickable.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}<span className="text-ink-faint"> · {m.category}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rule === 'one' && g.options.length > 0 && defaults === 0 && (
                  <span className="text-[12px] text-ink-faint">No default. The till opens on the first.</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => addGroup('one')} data-testid="meal-groups-add-one"><Plus size={14} className="mr-1" />Choose-one group</Button>
        <Button variant="outline" size="sm" onClick={() => addGroup('any')} data-testid="meal-groups-add-any"><Plus size={14} className="mr-1" />Optional group</Button>
        {groups.length > 0 && (
          <span className="ml-auto font-mono text-[12.5px] tabular-nums text-ink-soft" data-testid="meal-price-range">
            {low === high ? money(low) : `${money(low)} – ${money(high)}`}
          </span>
        )}
      </div>

      {groups.length > 0 && (
        <Notice tone="info" word="On the till" className="text-[12.5px]">
          The sheet opens on every tap of this item, and the bill shows one line. Each choice
          fires to its own section&rsquo;s station, labelled with the meal.
        </Notice>
      )}
    </div>
  );
};

/** Numbers as numbers, blanks gone — what the API stores. A group with no choices is not a
 *  choice, and the server refuses one, so it is dropped here rather than failing the save. */
export const normaliseMealGroups = (groups) => (groups || [])
  .filter((g) => g.label?.trim() && (g.options || []).length > 0)
  .map((g) => ({
    id: g.id,
    label: g.label.trim(),
    rule: g.rule === 'any' ? 'any' : 'one',
    ...(g.rule === 'any' && g.min != null && g.min !== '' ? { min: Number(g.min) } : {}),
    ...(g.rule === 'any' && g.max != null && g.max !== '' ? { max: Number(g.max) } : {}),
    options: (g.options || []).filter((o) => o.menu_item_id).map((o) => ({
      menu_item_id: o.menu_item_id,
      name: (o.name || '').trim(),
      category: o.category || '',
      price_delta: parseFloat(o.price_delta) || 0,
      ...(o.default ? { default: true } : {}),
    })),
  }));

export default MealGroupsEditor;
